import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailFromUrl = searchParams.get('email') || '';
  const [formData, setFormData] = useState({
    email: emailFromUrl,
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [token, setToken] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setChangingPassword(true);
    setError('');
    try {
      await axios.put(`${API_URL}/api/client/profile`, {
        current_password: formData.password,
        new_password: newPassword
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      // Password changed — proceed to dashboard
      navigate('/client/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/client/login`, {
        email: formData.email,
        password: formData.password
      });

      if (response.data.access_token) {
        localStorage.setItem('client_token', response.data.access_token);
        localStorage.setItem('client_data', JSON.stringify(response.data.client));
        
        // Check if must change password
        if (response.data.must_change_password) {
          setToken(response.data.access_token);
          setMustChangePassword(true);
        } else {
          navigate('/client/dashboard');
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  // Password change prompt after first login with temp password
  if (mustChangePassword) {
    return (
      <div className="page-container client-auth-page" data-testid="change-password-page">
        <div className="auth-card">
          <div className="auth-header">
            <img 
              src="https://customer-assets.emergentagent.com/job_071d5548-d385-4daa-a48c-997f978ccc70/artifacts/mlh0ioi5_JNT%20LOGO%20Only%20Round.png" 
              alt="JNT" 
              className="auth-logo"
            />
            <h1>Set Your Password</h1>
            <p>Please create a new password to secure your account</p>
          </div>

          <form onSubmit={handlePasswordChange} className="auth-form">
            {error && (
              <div className="error-banner" data-testid="password-error">
                <AlertCircle size={18} />
                <span>{error}</span>
              </div>
            )}

            <div className="form-group">
              <label>New Password</label>
              <div className="input-with-icon">
                <Lock size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 8 characters)"
                  required
                  minLength={8}
                  data-testid="new-password-input"
                />
                <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Confirm Password</label>
              <div className="input-with-icon">
                <Lock size={18} />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <Button type="submit" className="submit-btn" disabled={changingPassword} data-testid="change-password-submit">
              {changingPassword ? 'Updating...' : 'Set Password & Continue'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container client-auth-page" data-testid="client-login-page">
      <div className="auth-card">
        <div className="auth-header">
          <img 
            src="https://customer-assets.emergentagent.com/job_071d5548-d385-4daa-a48c-997f978ccc70/artifacts/mlh0ioi5_JNT%20LOGO%20Only%20Round.png" 
            alt="JNT" 
            className="auth-logo"
          />
          <h1>Welcome Back</h1>
          <p>Sign in to access your travel itineraries</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error-banner" data-testid="login-error">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          <div className="form-group">
            <label>Email</label>
            <div className="input-with-icon">
              <Mail size={18} />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                required
                data-testid="email-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Password</label>
            <div className="input-with-icon">
              <Lock size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                required
                data-testid="password-input"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                data-testid="toggle-password"
              >
                {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
            </div>
          </div>

          <Button type="submit" className="submit-btn" disabled={loading} data-testid="login-submit">
            {loading ? 'Signing In...' : 'Sign In'}
          </Button>
        </form>

        <div className="auth-footer">
          <p>Don't have an account? <a href="/client/register">Create Account</a></p>
        </div>
      </div>
    </div>
  );
};

export default ClientLogin;
